import { useState, type MouseEvent } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import TablePagination from '@mui/material/TablePagination';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Box from '@mui/material/Box';
import { useMapStore } from '@shared/hooks/useMapStore';
import { useRoutes } from '../hooks/useRoutes';
import type { RouteListItem } from '../types';
import RouteContextMenu from './RouteContextMenu';

const MAX_OVERLAYS = 2;

interface Props {
  onSelectRoute: (routeId: string) => void;
  onShowProducts: (routeId: string) => void;
  onCreateRoute: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function RouteList({ onSelectRoute, onShowProducts, onCreateRoute }: Props) {
  const [nameFilter, setNameFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, isLoading } = useRoutes({ name: nameFilter || undefined, page, size: rowsPerPage });

  const { selectedRouteId, visibleRouteOverlays, setSelectedRouteId, setVisibleRouteOverlays } =
    useMapStore();

  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; routeId: string } | null>(null);

  function handleRowClick(routeId: string) {
    setSelectedRouteId(routeId);
    onSelectRoute(routeId);
  }

  function handleToggleEye(routeId: string) {
    if (visibleRouteOverlays.includes(routeId)) {
      setVisibleRouteOverlays(visibleRouteOverlays.filter((id) => id !== routeId));
    } else if (visibleRouteOverlays.length < MAX_OVERLAYS) {
      setVisibleRouteOverlays([...visibleRouteOverlays, routeId]);
    }
  }

  function handleResetEyes() {
    setVisibleRouteOverlays([]);
  }

  function handleMenuOpen(e: MouseEvent<HTMLElement>, routeId: string) {
    e.stopPropagation();
    setMenuAnchor({ el: e.currentTarget, routeId });
  }

  function handleMenuClose() {
    setMenuAnchor(null);
  }

  const rows = data?.items ?? [];

  return (
    <div data-testid="route-list" className="flex flex-col gap-2">
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onCreateRoute}
          sx={{ textTransform: 'none', flexShrink: 0 }}
        >
          Create Route
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Filter by name..."
          value={nameFilter}
          onChange={(e) => {
            setNameFilter(e.target.value);
            setPage(0);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: 1 }}
        />
        {visibleRouteOverlays.length > 0 && (
          <Tooltip title="Reset all eye toggles">
            <IconButton size="small" onClick={handleResetEyes}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <List disablePadding>
        {isLoading ? (
          Array.from({ length: 5 }, (_, i) => (
            <Box key={i} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="text" width="30%" />
            </Box>
          ))
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            No routes found
          </Typography>
        ) : (
          rows.map((row) => (
            <RouteCard
              key={row.routeId}
              row={row}
              selected={selectedRouteId === row.routeId}
              isVisible={visibleRouteOverlays.includes(row.routeId)}
              canAddOverlay={visibleRouteOverlays.length < MAX_OVERLAYS}
              onClick={() => handleRowClick(row.routeId)}
              onToggleEye={() => handleToggleEye(row.routeId)}
              onMenuOpen={(e) => handleMenuOpen(e, row.routeId)}
            />
          ))
        )}
      </List>

      <TablePagination
        component="div"
        count={data?.totalItems ?? 0}
        page={page}
        labelRowsPerPage=""
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />

      <RouteContextMenu
        anchorEl={menuAnchor?.el ?? null}
        routeId={menuAnchor?.routeId ?? null}
        onClose={handleMenuClose}
        onShowProducts={onShowProducts}
      />
    </div>
  );
}

interface RouteCardProps {
  row: RouteListItem;
  selected: boolean;
  isVisible: boolean;
  canAddOverlay: boolean;
  onClick: () => void;
  onToggleEye: () => void;
  onMenuOpen: (e: MouseEvent<HTMLElement>) => void;
}

function RouteCard({
  row,
  selected,
  isVisible,
  canAddOverlay,
  onClick,
  onToggleEye,
  onMenuOpen,
}: RouteCardProps) {
  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      sx={{
        alignItems: 'flex-start',
        gap: 1,
        py: 1,
        px: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" noWrap sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
            {row.name}
          </Typography>
          {row.isCurrent && <Chip label="Active" color="success" size="small" />}
          {row.status === 'Creating' && <Chip label="Creating" color="warning" size="small" />}
        </Box>
        <Typography variant="body2" color="text.secondary" noWrap>
          {row.departurePort} — {row.arrivalPort}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {row.length.toFixed(1)} NM · {formatDate(row.createdAt)}
        </Typography>
      </Box>

      {row.status === 'Creating' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.5 }}>
          <CircularProgress size={20} />
          <Typography variant="caption" color="text.secondary">
            Creating...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.5 }}>
          <Tooltip
            title={isVisible ? 'Hide on map' : canAddOverlay ? 'Show on map' : 'Max 2 overlays'}
          >
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleEye();
                }}
                disabled={!isVisible && !canAddOverlay}
                color={isVisible ? 'primary' : 'default'}
              >
                {isVisible ? (
                  <VisibilityIcon fontSize="small" />
                ) : (
                  <VisibilityOffIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMenuOpen(e);
            }}
            aria-label="Route actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </ListItemButton>
  );
}
